import { useState } from 'react';
import { GitMerge, Cloud, ShieldAlert } from 'lucide-react';
import { resolveGuestMigrationDecision } from '../db/guestMigration';
import { useStore } from '../store/useStore';

interface GuestMigrationModalProps {
  userId: string;
  type: 'merge_prompt_needed' | 'faculty_mismatch';
  localCount: number;
  remoteCount: number;
  localFaculty?: string;
  remoteFaculty?: string;
  onResolved: () => void;
}

export function GuestMigrationModal({
  userId,
  type,
  localCount,
  remoteCount,
  localFaculty = 'psico',
  remoteFaculty = 'fcea',
  onResolved
}: GuestMigrationModalProps) {
  const [loading, setLoading] = useState(false);
  const loadData = useStore(s => s.loadData);
  const isMismatch = type === 'faculty_mismatch';

  const getFacultyName = (sigla: string) => {
    return sigla === 'psico'
      ? 'Facultad de Psicología (PSICO)'
      : 'Facultad de Ciencias Económicas y de Administración (FCEA)';
  };

  const handleDecision = async (decision: 'merge' | 'discard') => {
    setLoading(true);
    try {
      await resolveGuestMigrationDecision(userId, decision, remoteFaculty);
      await loadData();
      onResolved();
    } catch (err) {
      console.error('Error resolving guest migration decision:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 font-sans select-none animate-in fade-in duration-200">
        <div className="bg-white max-w-sm w-full rounded-3xl border border-slate-100 shadow-2xl p-6 text-center space-y-4 animate-in zoom-in-95 duration-200">
          <div className="w-12 h-12 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto shadow-xs" />
          <div>
            <h3 className="text-base font-bold text-slate-900">Sincronizando con la nube</h3>
            <p className="text-xs text-slate-500 mt-1">
              Fusionando asignaturas, evaluaciones y notas con tu cuenta...
            </p>
          </div>
          <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-[11px] font-mono rounded-full font-medium">
            Por favor esperá un instante
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4 font-sans select-none animate-in fade-in duration-200">
      <div className="bg-white max-w-lg w-full rounded-2xl border border-slate-200 shadow-2xl p-6 space-y-5 text-left">
        
        {/* Encabezado */}
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shrink-0">
            <ShieldAlert size={20} />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-bold text-slate-900 tracking-tight">
              {isMismatch ? 'Cambio de Facultad al Iniciar Sesión' : 'Encontramos datos locales de invitado'}
            </h2>
            <p className="text-xs text-slate-500 leading-relaxed">
              {isMismatch ? (
                <>
                  Navegabas como invitado en <strong className="text-slate-800 font-semibold">{getFacultyName(localFaculty)}</strong> ({localCount} asignaturas localmente), pero tu cuenta registrada pertenece a <strong className="text-slate-800 font-semibold">{getFacultyName(remoteFaculty)}</strong> ({remoteCount} asignaturas en la nube).
                </>
              ) : (
                <>
                  Tenés <strong className="text-slate-800 font-semibold">{localCount} asignaturas</strong> guardadas como invitado en este dispositivo y <strong className="text-slate-800 font-semibold">{remoteCount} asignaturas</strong> en tu cuenta de la nube ({getFacultyName(remoteFaculty)}).
                </>
              )}
            </p>
          </div>
        </div>

        {/* Indicador de estrategia */}
        <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200/70 text-xs text-slate-600 space-y-1">
          <span className="font-semibold text-slate-800 block">
            {isMismatch ? 'Aviso de protección de datos:' : '¿Cómo querés proceder?'}
          </span>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            {isMismatch
              ? 'Para evitar mezclar materias de facultades distintas, tus datos de invitado no se fusionarán con tu cuenta. Cargarás los datos de tu cuenta y podrás acceder a tus datos locales de invitado si cerrás sesión.'
              : 'Podés fusionar las materias de este dispositivo con las de tu cuenta o descartar los datos locales para quedarte únicamente con los de la nube.'}
          </p>
        </div>

        {/* Botones de Acción */}
        <div className="space-y-2.5 pt-1">
          {isMismatch ? (
            <button
              disabled={loading}
              onClick={() => handleDecision('discard')}
              className="w-full bg-primary hover:bg-primary-container text-white text-xs font-semibold px-4 py-3 rounded-xl transition-all shadow-xs flex items-center justify-between group cursor-pointer disabled:opacity-50"
            >
              <div className="flex items-center gap-2">
                <Cloud size={16} />
                <span>Continuar a mi cuenta de {remoteFaculty.toUpperCase()}</span>
              </div>
              <span className="text-[10px] font-mono opacity-80 group-hover:translate-x-0.5 transition-transform">
                Cargar nube →
              </span>
            </button>
          ) : (
            <>
              <button
                disabled={loading}
                onClick={() => handleDecision('merge')}
                className="w-full bg-primary hover:bg-primary-container text-white text-xs font-semibold px-4 py-3 rounded-xl transition-all shadow-xs flex items-center justify-between group cursor-pointer disabled:opacity-50"
              >
                <div className="flex items-center gap-2">
                  <GitMerge size={16} />
                  <span>Fusionar datos (Recomendado)</span>
                </div>
                <span className="text-[10px] font-mono opacity-80 group-hover:translate-x-0.5 transition-transform">
                  Conserva todo →
                </span>
              </button>

              <button
                disabled={loading}
                onClick={() => handleDecision('discard')}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold px-4 py-3 rounded-xl transition-all flex items-center justify-between group cursor-pointer disabled:opacity-50"
              >
                <div className="flex items-center gap-2">
                  <Cloud size={16} className="text-slate-500" />
                  <span>Descartar datos locales y usar solo la nube</span>
                </div>
                <span className="text-[10px] font-mono text-slate-400 group-hover:translate-x-0.5 transition-transform">
                  Solo nube →
                </span>
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
